import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2, Save, X, Plus, FileText, Target, Users } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function makeEmptyForm() {
  return {
    leads: [],
    contacts: [],
    opportunities: "",
    notes: "",
    attendees_count: 0,
    satisfaction_score: 0,
    key_takeaways: "",
    follow_up_actions: "",
  };
}

function formFromExisting(ex) {
  if (!ex) return makeEmptyForm();
  return {
    leads: ex.leads || [],
    contacts: ex.contacts || [],
    opportunities: ex.opportunities || "",
    notes: ex.notes || "",
    attendees_count: ex.attendees_count || 0,
    satisfaction_score: ex.satisfaction_score || 0,
    key_takeaways: ex.key_takeaways || "",
    follow_up_actions: ex.follow_up_actions || "",
  };
}

function TagList({ items, onRemove, variant }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(function renderTag(item, idx) {
        return (
          <Badge key={idx} variant={variant || "secondary"}>
            {item}
            <button
              onClick={function handleRemove() { onRemove(idx); }}
              className="ml-1 hover:text-destructive"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        );
      })}
    </div>
  );
}

export default function FollowUpDialog(props) {
  var evento = props.evento;
  var open = props.open;
  var onClose = props.onClose;
  var token = props.token;
  var onSaved = props.onSaved;

  var currentYear = new Date().getFullYear();

  var stateFollowups = useState([]);
  var followups = stateFollowups[0];
  var setFollowups = stateFollowups[1];

  var stateLoading = useState(true);
  var loading = stateLoading[0];
  var setLoading = stateLoading[1];

  var stateSaving = useState(false);
  var saving = stateSaving[0];
  var setSaving = stateSaving[1];

  var stateYear = useState(currentYear);
  var year = stateYear[0];
  var setYear = stateYear[1];

  var stateForm = useState(makeEmptyForm());
  var form = stateForm[0];
  var setForm = stateForm[1];

  var stateNewLead = useState("");
  var newLead = stateNewLead[0];
  var setNewLead = stateNewLead[1];

  var stateNewContact = useState("");
  var newContact = stateNewContact[0];
  var setNewContact = stateNewContact[1];

  useEffect(function onOpenChange() {
    if (open && evento) {
      fetchFollowups();
      if (evento.date) {
        try {
          setYear(new Date(evento.date).getFullYear());
        } catch (e) { /* ignore */ }
      }
    }
  }, [open, evento]);

  function fetchFollowups() {
    setLoading(true);
    fetch(API + "/api/eventos/" + evento.id + "/followups", {
      headers: { Authorization: "Bearer " + token },
    })
      .then(function(res) { return res.ok ? res.json() : []; })
      .then(function(data) {
        setFollowups(data);
        var existing = data.find(function(f) { return f.year === year; });
        setForm(formFromExisting(existing));
      })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }

  useEffect(function onYearChange() {
    var existing = followups.find(function(f) { return f.year === year; });
    setForm(formFromExisting(existing));
  }, [year]);

  function handleSave() {
    setSaving(true);
    var body = JSON.stringify(Object.assign({}, form, { year: year }));
    fetch(API + "/api/eventos/" + evento.id + "/followups", {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: body,
    })
      .then(function(res) {
        if (res.ok) {
          toast.success("Seguimiento guardado");
          fetchFollowups();
          if (onSaved) onSaved();
        } else {
          toast.error("Error al guardar");
        }
      })
      .catch(function() { toast.error("Error al guardar"); })
      .finally(function() { setSaving(false); });
  }

  function addLead() {
    if (newLead.trim()) {
      setForm(Object.assign({}, form, { leads: form.leads.concat([newLead.trim()]) }));
      setNewLead("");
    }
  }

  function addContact() {
    if (newContact.trim()) {
      setForm(Object.assign({}, form, { contacts: form.contacts.concat([newContact.trim()]) }));
      setNewContact("");
    }
  }

  function removeLead(idx) {
    setForm(Object.assign({}, form, { leads: form.leads.filter(function(_, i) { return i !== idx; }) }));
  }

  function removeContact(idx) {
    setForm(Object.assign({}, form, { contacts: form.contacts.filter(function(_, i) { return i !== idx; }) }));
  }

  var yearsSet = new Set();
  followups.forEach(function(f) { yearsSet.add(f.year); });
  yearsSet.add(currentYear);
  var years = Array.from(yearsSet).sort(function(a, b) { return b - a; });

  if (!evento) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Seguimiento: {evento.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-4">
          <Label>Año:</Label>
          <Select
            value={String(year)}
            onValueChange={function(v) { setYear(Number(v)); }}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(function(y) {
                return (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {followups.length > 0 && (
            <Badge variant="outline">
              {followups.length} edicion(es)
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Asistentes (n.)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.attendees_count}
                  onChange={function(e) {
                    setForm(Object.assign({}, form, { attendees_count: Number(e.target.value) }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Satisfaccion (1-10)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  value={form.satisfaction_score}
                  onChange={function(e) {
                    setForm(Object.assign({}, form, { satisfaction_score: Number(e.target.value) }));
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Target className="w-4 h-4 text-green-500" />
                Leads ({form.leads.length})
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newLead}
                  onChange={function(e) { setNewLead(e.target.value); }}
                  placeholder="Nombre o empresa del lead"
                  onKeyDown={function(e) {
                    if (e.key === "Enter") { e.preventDefault(); addLead(); }
                  }}
                />
                <Button size="sm" variant="outline" onClick={addLead}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <TagList items={form.leads} onRemove={removeLead} variant="secondary" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                Contactos ({form.contacts.length})
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newContact}
                  onChange={function(e) { setNewContact(e.target.value); }}
                  placeholder="Nombre del contacto"
                  onKeyDown={function(e) {
                    if (e.key === "Enter") { e.preventDefault(); addContact(); }
                  }}
                />
                <Button size="sm" variant="outline" onClick={addContact}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <TagList items={form.contacts} onRemove={removeContact} variant="outline" />
            </div>

            <div className="space-y-2">
              <Label>Oportunidades detectadas</Label>
              <Textarea
                value={form.opportunities}
                onChange={function(e) {
                  setForm(Object.assign({}, form, { opportunities: e.target.value }));
                }}
                rows={2}
                placeholder="Oportunidades de negocio..."
              />
            </div>

            <div className="space-y-2">
              <Label>Conclusiones clave</Label>
              <Textarea
                value={form.key_takeaways}
                onChange={function(e) {
                  setForm(Object.assign({}, form, { key_takeaways: e.target.value }));
                }}
                rows={2}
                placeholder="Ideas principales..."
              />
            </div>

            <div className="space-y-2">
              <Label>Acciones de seguimiento</Label>
              <Textarea
                value={form.follow_up_actions}
                onChange={function(e) {
                  setForm(Object.assign({}, form, { follow_up_actions: e.target.value }));
                }}
                rows={2}
                placeholder="Proximos pasos..."
              />
            </div>

            <div className="space-y-2">
              <Label>Notas adicionales</Label>
              <Textarea
                value={form.notes}
                onChange={function(e) {
                  setForm(Object.assign({}, form, { notes: e.target.value }));
                }}
                rows={2}
                placeholder="Observaciones generales..."
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={function() { onClose(false); }}
          >
            <X className="w-4 h-4 mr-2" />
            Cerrar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
